import { useField, useFormikContext } from 'formik';
import React__default, { useCallback, useMemo } from 'react';
import { TimeInput } from '../inputs/TimeInput.js';

/**
 * Allows the user to select and edit a specific time. Each part of a time value is displayed in an individually editable segment.
 * Wraps the `TimeInput` component and provides formik integration.
 *
 * @example
 * <TimeField name="scheduled" label="Scheduled" />
 */ const TimeField = ({ name , error , reserveMessageSpace =true , onFieldChange , onFieldBlur , ...props })=>{
    const [field, meta] = useField(name);
    const formik = useFormikContext();
    const onChange = useCallback((v)=>{
        if (onFieldChange !== undefined) {
            onFieldChange(v);
        }
        formik.setFieldTouched(name, true, true);
        // Manually update the formik values as a workaround to formik removing the field from values completely
        // if setting the field value to undefined via the 'standard' field helpers.
        // This is a bug in formik, see https://github.com/jaredpalmer/formik/issues/2523#issuecomment-1443473902
        if (typeof formik.values === "object") {
            formik.setValues({
                ...formik.values,
                [name]: v
            });
        } else {
            formik.setValues({
                [name]: v
            });
        }
    }, [
        formik,
        name,
        onFieldChange
    ]);
    const onBlur = useCallback((v)=>{
        if (onFieldBlur !== undefined) {
            onFieldBlur(v);
        }
        formik.setFieldTouched(name, true, true);
    }, [
        formik,
        name,
        onFieldBlur
    ]);
    const errorMessage = useMemo(()=>{
        if (meta.touched && !!meta.error) {
            const metaError = meta.error;
            const errorMessage = typeof metaError === "string" ? metaError : metaError[Object.keys(metaError)[0]];
            return errorMessage === "" ? true : errorMessage;
        } else {
            return !!error ? error : false;
        }
    }, [
        error,
        meta
    ]);
    return React__default.createElement(TimeInput, {
        ...props,
        "data-hd-time-field": true,
        error: errorMessage,
        reserveMessageSpace: reserveMessageSpace,
        value: field.value,
        onChange: onChange,
        onBlur: onBlur
    });
};
TimeField.dataId = "data-hd-time-field";
TimeField.displayName = "TimeField";

export { TimeField };
